import React, { useEffect } from "react";
import { useState } from "react";
import { useAuth } from "shared/contexts/AuthProvider";
import { Alert, Button, Form, Input, Tooltip, Typography } from "antd";
import { CopyOutlined, ReloadOutlined } from "@ant-design/icons";
import { changePassword, getApiKey, generateNewApiKey } from "shared/services/auth-services/auth_service";

const { Text } = Typography;

export const UserProfileEditPage = () => {
  const [error, setError] = useState("");
  const [processing, setProcessing] = useState(false);
  const { auth, user } = useAuth();
  const [message, setMessage] = useState("");

  // API key state
  const [apiKey, setApiKey] = useState(null);
  const [apiKeyError, setApiKeyError] = useState("");
  const [apiKeyMessage, setApiKeyMessage] = useState("");
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setApiKeyLoading(true);
    getApiKey(auth.token)
      .then((key) => setApiKey(key))
      .catch((err) => setApiKeyError(err.message))
      .finally(() => setApiKeyLoading(false));
  }, [auth.token]);

  const handleCopyApiKey = () => {
    if (apiKey) {
      navigator.clipboard.writeText(apiKey).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    }
  };

  const handleGenerateApiKey = () => {
    setApiKeyLoading(true);
    setApiKeyError("");
    setApiKeyMessage("");
    generateNewApiKey(auth.token)
      .then((key) => {
        setApiKey(key);
        setApiKeyMessage("New API key generated successfully");
      })
      .catch((err) => setApiKeyError(err.message))
      .finally(() => setApiKeyLoading(false));
  };

  const handleFormSubmit = ({ password, newPassword }) => {
    setProcessing(true);
    setError("");
    setMessage("");

    changePassword(auth.token, password, newPassword)
      .then((result) => {
        setMessage("Password changed success");
        setError("");
      })
      .catch((err) => {
        setError(err.message);
      })
      .finally(() => {
        setProcessing(false);
      });
  };

  return (
    <div className="login-container">
      <h2>Change password</h2>
      <Form
        layout="vertical"
        onFinish={handleFormSubmit}
        className="login-body"
      >
        {error && <Alert type="error" message={error} banner />}
        {message && <Alert type="success" message={message} banner />}

        {/* <div className="email-container">Username: {auth.username}</div> */}

        <div className="password-container">
          <Form.Item
            label="Current password"
            name="password"
            rules={[
              {
                required: true,
                message: "Please input your current password!",
              },
            ]}
          >
            <Input.Password />
          </Form.Item>
        </div>
        <div className="password-container">
          <Form.Item
            label="New password"
            name="newPassword"
            rules={[
              {
                required: true,
                message: "Please input your new password!",
              },
            ]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirmPassword"
            label="Confirm new password"
            dependencies={["newPassword"]}
            hasFeedback
            rules={[
              {
                required: true,
                message: "Please confirm your new password!",
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("newPassword") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(
                      "The confirm password that you entered do not match!"
                    )
                  );
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </div>

        <div className="login-btn-container">
          <input value="Change Password" type="submit" />
        </div>
      </Form>

      <h2>API Key</h2>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {apiKeyError && <Alert type="error" message={apiKeyError} banner />}
        {apiKeyMessage && <Alert type="success" message={apiKeyMessage} banner />}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Text
            code
            style={{
              flex: 1,
              padding: "6px 10px",
              border: "1px solid #d9d9d9",
              borderRadius: 6,
              background: "#fafafa",
              wordBreak: "break-all",
              minHeight: 36,
              display: "block",
            }}
          >
            {apiKeyLoading ? "Loading..." : (apiKey ?? "—")}
          </Text>
          <Tooltip title={copied ? "Copied!" : "Copy API key"}>
            <Button
              icon={<CopyOutlined />}
              onClick={handleCopyApiKey}
              disabled={!apiKey || apiKeyLoading}
            />
          </Tooltip>
        </div>
        <Tooltip title="Generating a new key will invalidate the current one">
          <Button
            icon={<ReloadOutlined />}
            onClick={handleGenerateApiKey}
            loading={apiKeyLoading}
            danger
          >
            Generate New API Key
          </Button>
        </Tooltip>
      </div>
    </div>
  );
};
